import { useMemo } from 'react';

import {
  SelectionCategories,
  type SelectionList,
  type SelectionListIdNames,
} from '@revelio/iso-utility';

import { useCustomSkillTaxonomyTree } from '../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useRoleSelectionList } from '../../../../shared/selection-lists/hooks/useRoleSelectionList';
import { useSelectionLists } from '../../../../shared/selection-lists/hooks/useSelectionLists';
import type { GqlSelectionListKey } from '../../../../shared/selection-lists/types';
import { useRoleTaxonomySelection } from '../../../../shared/user-preferences/hooks/useRoleTaxonomySelection';
import { SELECTABLE_API_FILTERS } from '../utils/selectableFilters';
import { ALL_STATIC_SELECTION_LISTS } from '../utils/staticSelectionLists';

export const useAllSelectionLists = (): SelectionList[] => {
  const roleTaxonomySelection = useRoleTaxonomySelection();
  const { customSkillTree } = useCustomSkillTaxonomyTree();

  const apiSelectionLists = useSelectionLists(
    SELECTABLE_API_FILTERS as GqlSelectionListKey[]
  );
  const roleSelectionLists = useRoleSelectionList(roleTaxonomySelection);
  const allSelectionLists = useMemo(() => {
    const apiSelectionListItems = Object.entries(apiSelectionLists ?? {}).map(
      ([id, list]) => ({
        id,
        value: list,
      })
    );

    // Build custom skill selection lists (skill_k75 -> L0, skill_k700 -> L1, skill_k3000 -> L2, skill_k15000 -> L3)
    const customSkillSelectionListItems: SelectionList[] = (() => {
      if (!customSkillTree || customSkillTree.length === 0) return [];

      const level0 = customSkillTree.map((n) => ({
        id: n.id,
        longName: n.name,
      }));

      const level1 = customSkillTree.flatMap((n0) =>
        (n0.children || []).map((n1) => {
          const parts = String(n1.id).split('_');
          const id = parts[parts.length - 1];
          return { id, longName: n1.name, skill_0: n0.id };
        })
      );

      const level2 = customSkillTree.flatMap((n0) =>
        (n0.children || []).flatMap((n1) =>
          (n1.children || []).map((n2) => {
            const parts = String(n2.id).split('_');
            const id = parts[parts.length - 1];
            const parentParts = String(n1.id).split('_');
            const parentId = parentParts[parentParts.length - 1];
            return { id, longName: n2.name, skill_1: parentId };
          })
        )
      );

      const level3 = customSkillTree.flatMap((n0) =>
        (n0.children || []).flatMap((n1) =>
          (n1.children || []).flatMap((n2) =>
            (n2.children || []).map((n3) => {
              const parts = String(n3.id).split('_');
              const id = parts[parts.length - 1];
              const parentParts = String(n2.id).split('_');
              const parentId = parentParts[parentParts.length - 1];
              return { id, longName: n3.name, skill_2: parentId };
            })
          )
        )
      );

      return [
        {
          id: SelectionCategories.SKILL_0,
          value: level0,
        },
        {
          id: SelectionCategories.SKILL_1,
          value: level1,
          parent: 'skill_0',
        },
        {
          id: SelectionCategories.SKILL_2,
          value: level2,
          parent: 'skill_1',
        },
        {
          id: SelectionCategories.SKILL_3,
          value: level3,
          parent: 'skill_2',
        },
      ] as SelectionList[];
    })();

    const allowedRoleIds = [
      'role_k7',
      'role_k150',
      'role_k1500',
      'role_k15000',
    ];
    const roleSelectionListItems = Object.entries(roleSelectionLists ?? {})
      .filter(([id]) => allowedRoleIds.includes(id))
      .map(([id, list]) => ({
        id,
        value: list,
      }));

    // If custom skills are available, replace API skill lists with custom ones
    const skillIds = new Set<SelectionList['id']>([
      SelectionCategories.SKILL_0,
      SelectionCategories.SKILL_1,
      SelectionCategories.SKILL_2,
      SelectionCategories.SKILL_3,
    ]);

    const apiMinusSkills = customSkillSelectionListItems.length
      ? apiSelectionListItems.filter(
          (item) => !skillIds.has(item?.id as SelectionListIdNames)
        )
      : apiSelectionListItems;

    return [
      ...apiMinusSkills,
      ...(customSkillSelectionListItems.length
        ? customSkillSelectionListItems
        : []),
      ...roleSelectionListItems,
      ...ALL_STATIC_SELECTION_LISTS,
    ] as SelectionList[];
  }, [apiSelectionLists, roleSelectionLists, customSkillTree]);

  return allSelectionLists;
};
